type FeatureItem = { feature: string; available: boolean; count?: number; isComingSoon?: boolean };
type PlanFeature = string;

// ─── Currency ────────────────────────────────────────────────────────────────
/** Exchange rate used for AMD → USD display. Change this value to update all prices. */
export const AMD_TO_USD_RATE = 400;

/** Converts an AMD amount to USD, rounded to the nearest dollar. */
export const amdToUsd = (amd: number): number => Math.round(amd / AMD_TO_USD_RATE);

// ─── String-only feature lists for PLANS (SubscriptionPage bullet list) ────────

const BASIC_PLAN_FEATURES: PlanFeature[] = [
    "pricing-features.all-social-links",
    "pricing-features.webpage",
    "pricing-features.support",
    "pricing-features.editable-data",
    "pricing-features.analytics",
    "pricing-features.wallet-pay",
    "pricing-features.languages",
    "pricing-features.contacts",
];

const BUSINESS_PLAN_EXTRA_FEATURES: PlanFeature[] = [
    "pricing-features.feedback",
    "pricing-features.branches",
    "pricing-features.employes",
    "pricing-features.services-menu",
    "pricing-features.services-order",
];

const PREMIUM_PLAN_EXTRA_FEATURES: PlanFeature[] = [
    "pricing-features.ratings",
    "pricing-features.ratings-analytics",
];

const BUSINESS_PLAN_FEATURES: PlanFeature[] = [...BASIC_PLAN_FEATURES, ...BUSINESS_PLAN_EXTRA_FEATURES];
const PREMIUM_PLAN_FEATURES: PlanFeature[] = [...BUSINESS_PLAN_FEATURES, ...PREMIUM_PLAN_EXTRA_FEATURES];

// ─── Delta feature sets for SUBSCRIPTION_PACKAGES / TEAM_PACKAGES ──────────────
// Each set lists ONLY what this tier adds over the previous one.

/** sub_free — standalone (no previous tier) */
const FREE_FEATURES: FeatureItem[] = [
    { feature: "pricing-features.links-count", available: true, count: 1 },
    { feature: "pricing-features.social-links-count", available: true, count: 2 },
    { feature: "pricing-features.webpage", available: true },
    { feature: "pricing-features.support", available: true },
    { feature: "pricing-features.editable-data", available: true },
    { feature: "pricing-features.wallet-pay", available: false, isComingSoon: true },
    // { feature: "pricing-features.analytics", available: false },
    // { feature: "pricing-features.digital-card-2", available: false },
];

/** sub_basic — what it adds over sub_free */
const STANDARD_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-count", available: true, count: 3 },
    { feature: "pricing-features.all-social-links", available: true },
    { feature: "pricing-features.analytics", available: true },
    { feature: "pricing-features.wallet-pay", available: true, isComingSoon: true },
    { feature: "pricing-features.languages", available: true },
    { feature: "pricing-features.contacts", available: true },
];

/** sub_business — what it adds over sub_basic */
const BUSINESS_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-count", available: true, count: 10 },
    { feature: "pricing-features.feedback", available: true },
    { feature: "pricing-features.branches", available: true },
    { feature: "pricing-features.employes", available: true },
    { feature: "pricing-features.services-menu", available: true },
    { feature: "pricing-features.services-order", available: true },
];

/** sub_premium — what it adds over sub_business */
const PREMIUM_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-count", available: true, count: 15 },
    { feature: "pricing-features.ratings", available: true },
    { feature: "pricing-features.ratings-analytics", available: true },
];

/** team_starter — what it adds over sub_business (separate team track) */
const TEAM_STARTER_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-per-member-25", available: true },
    { feature: "pricing-features.ratings", available: true },
    { feature: "pricing-features.ratings-analytics", available: true },
];

/** team_pro — what it adds over team_starter */
const TEAM_PRO_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-per-member-45", available: true },
];

/** team_enterprise — what it adds over team_pro */
const TEAM_ENTERPRISE_DELTA: FeatureItem[] = [
    { feature: "pricing-features.links-per-member-100", available: true },
];

// ─── One-time NFC card packages ─────────────────────────────────────────────────

const ESSENTIAL_FEATURES: FeatureItem[] = [
    { feature: "pricing-features.digital-card", available: true },
    { feature: "pricing-features.webpage", available: true },
    { feature: "pricing-features.support", available: true },
    { feature: "pricing-features.dashboard", available: true },
    { feature: "pricing-features.editable-data", available: true },
    { feature: "pricing-features.feedback", available: true },
    { feature: "pricing-features.languages", available: true },
    { feature: "pricing-features.contacts", available: true },
    { feature: "pricing-features.analytics", available: true },
    { feature: "pricing-features.free-delivery", available: true },
    { feature: "pricing-features.wallet-pay", available: false, isComingSoon: true },
    // { feature: "pricing-features.branches", available: false },
    // { feature: "pricing-features.employes", available: false },
    // { feature: "pricing-features.services-menu", available: false },
    // { feature: "pricing-features.services-order", available: false },
];

const BUSINESS_PACKAGE_DELTA: FeatureItem[] = [
    { feature: "pricing-features.digital-card-3", available: true },
    { feature: "pricing-features.global-dashboard-1", available: true },
    { feature: "pricing-features.branches", available: true },
    { feature: "pricing-features.employes", available: true },
    { feature: "pricing-features.services-menu", available: true },
    { feature: "pricing-features.services-order", available: true },
];

const CORPORATE_PACKAGE_DELTA: FeatureItem[] = [
    { feature: "pricing-features.premium-nfc-10", available: true },
    { feature: "pricing-features.ratings", available: true },
    { feature: "pricing-features.ratings-analytics", available: true },
    { feature: "pricing-features.wallet-pay", available: true, isComingSoon: true },
];

export const PACKAGES = [
    {
        name: "titles.essential-package",
        price: 22900,
        description: "titles.essential-package-desc",
        includes: ESSENTIAL_FEATURES,
        details: "pricing-features.details-essential",
        type: "basic"
    },
    {
        name: "titles.business-package",
        price: 42900,
        description: "titles.business-package-desc",
        inheritsLabel: "titles.essential-package",
        includes: BUSINESS_PACKAGE_DELTA,
        details: "pricing-features.details-business",
        type: "business",
        additionalCards: "pricing-features.additional-cards",
        suggested: true
    },
    {
        name: "titles.corporate-package",
        price: 82900,
        pricePeriod: "֏ / one-time",
        description: "titles.corporate-package-desc",
        inheritsLabel: "titles.business-package",
        includes: CORPORATE_PACKAGE_DELTA,
        details: "pricing-features.details-corporate",
        type: "corporate",
        additionalCards: "pricing-features.additional-cards",
    }
];

// ─── Subscription plans (used by SubscriptionPage bullet list) ──────────────────

export const PLANS = [
    {
        name: "titles.basic-subscription",
        priceBoughtPackage: 490,
        priceNoPackage: 890,
        yearlyPriceBoughtPackage: 4900,
        yearlyPriceNoPackage: 8900,
        type: "sub_basic",
        features: [
            "pricing-features.links-3",
            ...BASIC_PLAN_FEATURES,
        ]
    },
    {
        name: "titles.business-subscription",
        priceBoughtPackage: 890,
        priceNoPackage: 1290,
        yearlyPriceBoughtPackage: 8900,
        yearlyPriceNoPackage: 12900,
        type: "sub_business",
        features: [
            "pricing-features.links-10",
            ...BUSINESS_PLAN_FEATURES,
        ]
    },
    {
        name: "titles.premium-subscription",
        priceBoughtPackage: 2400,
        priceNoPackage: 4900,
        yearlyPriceBoughtPackage: 24000,
        yearlyPriceNoPackage: 49000,
        type: "sub_premium",
        features: [
            "pricing-features.links-15",
            ...PREMIUM_PLAN_FEATURES,
        ]
    },
    {
        name: "titles.team-starter",
        priceBoughtPackage: 5900,
        priceNoPackage: 10900,
        yearlyPriceBoughtPackage: 59000,
        yearlyPriceNoPackage: 109000,
        type: "sub_team_starter",
        isTeamPlan: true,
        features: [
            "pricing-features.links-per-member-25",
            ...PREMIUM_PLAN_FEATURES,
        ]
    },
    {
        name: "titles.team-pro",
        priceBoughtPackage: 8900,
        priceNoPackage: 15900,
        yearlyPriceBoughtPackage: 89000,
        yearlyPriceNoPackage: 159000,
        type: "sub_team_pro",
        isTeamPlan: true,
        features: [
            "pricing-features.links-per-member-45",
            ...PREMIUM_PLAN_FEATURES,
        ]
    },
    {
        name: "titles.team-enterprise",
        priceBoughtPackage: 13900,
        priceNoPackage: 25900,
        yearlyPriceBoughtPackage: 139000,
        yearlyPriceNoPackage: 259000,
        type: "sub_team_enterprise",
        isTeamPlan: true,
        suggested: true,
        features: [
            "pricing-features.links-per-member-100",
            ...PREMIUM_PLAN_FEATURES,
        ]
    },
];

export const FEATURES = [
    "pricing-features.digital-card",
    "pricing-features.webpage",
    "pricing-features.support",
    "pricing-features.dashboard",
    "pricing-features.editable-data",
    "pricing-features.feedback",
    "pricing-features.languages",
    "pricing-features.contacts",
    "pricing-features.analytics",
    "pricing-features.included-first-year",
    "pricing-features.free-delivery",
    "pricing-features.branches",
    "pricing-features.global-dashboard",
    "pricing-features.premium-nfc-10",
    "pricing-features.nfc-accessories",
    "pricing-features.links-count",
    "pricing-features.social-links-count",
    "pricing-features.all-social-links",
    "pricing-features.ratings",
    "pricing-features.ratings-analytics",
];

// ─── Individual subscription packages (Packages page card list) ────────────────

export const SUBSCRIPTION_PACKAGES = [
    {
        name: "titles.free-subscription",
        price: 0,
        description: "titles.free-subscription-desc",
        includes: FREE_FEATURES,
        details: "pricing-features.details-free",
        type: "sub_free"
    },
    {
        name: "titles.basic-subscription",
        price: 890,
        description: "titles.basic-subscription-desc",
        inheritsLabel: "titles.free-subscription",
        includes: STANDARD_DELTA,
        details: "pricing-features.details-basic",
        type: "sub_basic"
    },
    {
        name: "titles.business-subscription",
        price: 1290,
        description: "titles.business-subscription-desc",
        inheritsLabel: "titles.basic-subscription",
        includes: BUSINESS_DELTA,
        details: "pricing-features.details-business_sub",
        type: "sub_business",
        suggested: true
    },
    {
        name: "titles.premium-subscription",
        price: 4900,
        description: "titles.premium-subscription-desc",
        inheritsLabel: "titles.business-subscription",
        includes: PREMIUM_DELTA,
        details: "pricing-features.details-premium_sub",
        type: "sub_premium"
    }
];

// ─── Team packages (Packages page — Teams tab) ─────────────────────────────────

export const TEAM_PACKAGES = [
    {
        name: "titles.team-starter",
        price: 10900,
        description: "titles.team-starter-desc",
        inheritsLabel: "titles.business-subscription",
        includes: TEAM_STARTER_DELTA,
        details: "pricing-features.details-team",
        type: "sub_team_starter",
        isTeamPlan: true,
    },
    {
        name: "titles.team-pro",
        price: 15900,
        description: "titles.team-pro-desc",
        inheritsLabel: "titles.team-starter",
        includes: TEAM_PRO_DELTA,
        details: "pricing-features.details-team",
        type: "sub_team_pro",
        isTeamPlan: true,
        suggested: true,
    },
    {
        name: "titles.team-enterprise",
        price: 25900,
        description: "titles.team-enterprise-desc",
        inheritsLabel: "titles.team-pro",
        includes: TEAM_ENTERPRISE_DELTA,
        details: "pricing-features.details-team-enterprise",
        type: "sub_team_enterprise",
        isTeamPlan: true,
    },
];
